import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface AlquilerRequest {
  id: string;
  nombre: string;
  telefono: string;
  email: string;
  fecha_recogida: string;
  fecha_devolucion: string;
  vehiculo_preferido: string;
  conductores_adicionales: string;
  lugar_recogida: string;
  combustible_preferido: string;
  comentarios: string;
  estado: 'pendiente' | 'confirmado' | 'cancelado';
  leido: boolean;
  created_at: string;
}

const ESTADO_COLORS = {
  pendiente:  'bg-yellow-400/15 text-yellow-400 border-yellow-400/30',
  confirmado: 'bg-green-500/15 text-green-400 border-green-500/30',
  cancelado:  'bg-red-500/15 text-red-400 border-red-500/30',
};

const ESTADO_LABELS = {
  pendiente:  'Pendiente',
  confirmado: 'Confirmado',
  cancelado:  'Cancelado',
};

interface Props {
  onBadgeUpdate?: (n: number) => void;
}

const AlquilerRequestsTab = ({ onBadgeUpdate }: Props) => {
  const [requests, setRequests] = useState<AlquilerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AlquilerRequest | null>(null);
  const [filter, setFilter] = useState<'todos' | 'pendiente' | 'confirmado' | 'cancelado'>('todos');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('TALLERMECANICOKEVIN_alquiler_requests')
      .select('*')
      .order('created_at', { ascending: false });
    const rows = (data ?? []) as AlquilerRequest[];
    setRequests(rows);
    const unread = rows.filter((r) => !r.leido).length;
    onBadgeUpdate?.(unread);
    setLoading(false);
  }, [onBadgeUpdate]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const markRead = async (req: AlquilerRequest) => {
    if (!req.leido) {
      await supabase.from('TALLERMECANICOKEVIN_alquiler_requests').update({ leido: true }).eq('id', req.id);
      setRequests((p) => p.map((r) => r.id === req.id ? { ...r, leido: true } : r));
      onBadgeUpdate?.(requests.filter((r) => !r.leido && r.id !== req.id).length);
    }
    setSelected(req);
  };

  const changeEstado = async (id: string, estado: AlquilerRequest['estado']) => {
    await supabase.from('TALLERMECANICOKEVIN_alquiler_requests').update({ estado }).eq('id', id);
    setRequests((p) => p.map((r) => r.id === id ? { ...r, estado } : r));
    setSelected((p) => p ? { ...p, estado } : null);
  };

  const filtered = filter === 'todos' ? requests : requests.filter((r) => r.estado === filter);
  const pendingCount = requests.filter((r) => r.estado === 'pendiente').length;
  const unreadCount = requests.filter((r) => !r.leido).length;

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const formatDateShort = (d: string) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
    </div>
  );

  return (
    <div className="flex gap-5 h-full" style={{ minHeight: '60vh' }}>
      {/* Left panel */}
      <div className={`flex flex-col ${selected ? 'hidden md:flex md:w-72 lg:w-80' : 'w-full md:w-72 lg:w-80'} flex-shrink-0`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider">Solicitudes de alquiler</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{requests.length} total · {unreadCount} sin leer</p>
          </div>
          <button onClick={fetchRequests} className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white cursor-pointer transition-colors">
            <i className="ri-refresh-line text-base" />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-3 bg-zinc-900 border border-zinc-800 p-1">
          {(['todos', 'pendiente', 'confirmado', 'cancelado'] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex-1 py-1.5 text-[10px] font-bold uppercase tracking-wider cursor-pointer transition-colors whitespace-nowrap rounded ${
                filter === f ? 'bg-yellow-400 text-black' : 'text-zinc-500 hover:text-zinc-300'
              }`}>
              {f === 'todos' ? `Todos (${requests.length})` : f === 'pendiente' ? `Pend. (${pendingCount})` : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="flex-1 space-y-1.5 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-zinc-600">
              <i className="ri-steering-2-line text-3xl mb-2" />
              <p className="text-sm">Sin solicitudes</p>
            </div>
          ) : filtered.map((req) => (
            <button key={req.id} onClick={() => markRead(req)}
              className={`w-full text-left p-3 border transition-all cursor-pointer ${
                selected?.id === req.id
                  ? 'bg-zinc-800 border-yellow-400/30'
                  : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
              }`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  {!req.leido && <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />}
                  <span className="text-white text-xs font-bold truncate">{req.nombre || 'Sin nombre'}</span>
                </div>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 border rounded flex-shrink-0 ${ESTADO_COLORS[req.estado]}`}>
                  {ESTADO_LABELS[req.estado]}
                </span>
              </div>
              <p className="text-zinc-500 text-[10px] truncate">{req.fecha_recogida ? `Recogida: ${formatDateShort(req.fecha_recogida)}` : 'Sin fecha'}</p>
              <p className="text-zinc-600 text-[10px] mt-0.5">{formatDate(req.created_at)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Right detail panel */}
      {selected ? (
        <div className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 p-5 overflow-y-auto">
          <div className="flex items-start justify-between mb-5">
            <div>
              <button onClick={() => setSelected(null)} className="md:hidden flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-3 cursor-pointer">
                <i className="ri-arrow-left-line" /> Volver
              </button>
              <h3 className="text-white font-bold text-base">{selected.nombre}</h3>
              <p className="text-zinc-500 text-xs mt-0.5">{formatDate(selected.created_at)}</p>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {(['pendiente', 'confirmado', 'cancelado'] as const).map((e) => (
                <button key={e} onClick={() => changeEstado(selected.id, e)}
                  className={`text-[10px] font-bold uppercase px-2.5 py-1.5 border cursor-pointer transition-all whitespace-nowrap ${
                    selected.estado === e ? ESTADO_COLORS[e] : 'border-zinc-700 text-zinc-500 hover:text-zinc-300'
                  }`}>
                  {ESTADO_LABELS[e]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[
              { label: 'Teléfono', value: selected.telefono, icon: 'ri-phone-line', href: `tel:${selected.telefono}` },
              { label: 'Email', value: selected.email, icon: 'ri-mail-line', href: `mailto:${selected.email}` },
              { label: 'Fecha de recogida', value: formatDateShort(selected.fecha_recogida), icon: 'ri-calendar-event-line' },
              { label: 'Fecha de devolución', value: formatDateShort(selected.fecha_devolucion), icon: 'ri-calendar-check-line' },
              { label: 'Vehículo preferido', value: selected.vehiculo_preferido || '—', icon: 'ri-car-line' },
              { label: 'Combustible', value: selected.combustible_preferido || '—', icon: 'ri-gas-station-line' },
              { label: 'Lugar de recogida', value: selected.lugar_recogida || '—', icon: 'ri-map-pin-line' },
              { label: 'Conductores adicionales', value: selected.conductores_adicionales || '0', icon: 'ri-user-add-line' },
            ].map((field) => (
              <div key={field.label} className="bg-zinc-800/50 border border-zinc-700 px-4 py-3">
                <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider mb-1">{field.label}</p>
                {field.href ? (
                  <a href={field.href} className="text-yellow-400 text-sm font-semibold hover:text-yellow-300 transition-colors flex items-center gap-1.5">
                    <i className={`${field.icon} text-xs`} />{field.value}
                  </a>
                ) : (
                  <p className="text-white text-sm font-semibold flex items-center gap-1.5">
                    <i className={`${field.icon} text-zinc-500 text-xs`} />{field.value}
                  </p>
                )}
              </div>
            ))}
          </div>

          {selected.comentarios && (
            <div className="bg-zinc-800/50 border border-zinc-700 px-4 py-3 mb-4">
              <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider mb-2">Comentarios</p>
              <p className="text-zinc-300 text-sm leading-relaxed">{selected.comentarios}</p>
            </div>
          )}

          <div className="flex gap-2 mt-4 flex-wrap">
            {selected.telefono && (
              <a href={`tel:${selected.telefono}`}
                className="flex items-center gap-1.5 px-4 py-2 bg-yellow-400 text-black text-xs font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors whitespace-nowrap">
                <i className="ri-phone-line" /> Llamar
              </a>
            )}
            {selected.telefono && (
              <a href={`https://wa.me/34${selected.telefono.replace(/\D/g, '')}`} target="_blank" rel="nofollow noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-black uppercase tracking-wider hover:bg-green-500 transition-colors whitespace-nowrap">
                <i className="ri-whatsapp-line" /> WhatsApp
              </a>
            )}
            {selected.email && (
              <a href={`mailto:${selected.email}`}
                className="flex items-center gap-1.5 px-4 py-2 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-bold uppercase tracking-wider hover:bg-zinc-700 transition-colors whitespace-nowrap">
                <i className="ri-mail-line" /> Email
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center text-zinc-700 bg-zinc-900/40 border border-zinc-800/50">
          <div className="text-center">
            <i className="ri-steering-2-line text-4xl mb-3 block" />
            <p className="text-sm">Selecciona una solicitud para ver los detalles</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlquilerRequestsTab;
